import { useEffect, useRef } from "react";

import { goneWords, useHandedTo } from "@/lib/caseGone";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

import { Button } from "./Button";
import styles from "./CaseGone.module.css";
import { EmptyState } from "./EmptyState";
import { StateMessage } from "./StateMessage";

/**
 * A Vorgang's spoke — the board, the calculation — whose Vorgang answers 404
 * (round-5 screen check, R5-6): the same state the record shows, named where
 * the new owner is known („Vorgang übergeben — … liegt jetzt bei Chiara
 * Beispiel"), with the one way on that still leads somewhere, „Zum
 * Register". The board said only „Vorgang nicht gefunden", and the crumb's
 * „← Zur Akte" above it led to the next dead end.
 *
 * The focus comes here (final review of Plan D, I-1): the page that had the
 * Vorgang is gone, and the gesture that found out may have been the
 * person's own.
 */
export function CaseGone({ caseId, onRegister }: { caseId: string | undefined; onRegister: () => void }) {
  const { handedTo, asking } = useHandedTo(caseId, true);
  const words = goneWords(handedTo);
  useDocumentTitle(asking ? null : words.title);
  if (asking) return <StateMessage title="Vorgang wird geladen …" />;
  return <GoneAnchor key={words.title} title={words.title} text={words.text} onRegister={onRegister} />;
}

function GoneAnchor({ title, text, onRegister }: { title: string; text: string; onRegister: () => void }) {
  const anchor = useRef<HTMLDivElement>(null);
  useEffect(() => {
    anchor.current?.focus();
  }, []);
  return (
    // A dead end with one way on, as the empty state it is (UX gate round 4,
    // #11) — not a grey notice over an empty page.
    <div ref={anchor} tabIndex={-1} role="group" aria-label={title} className={styles.anchor}>
      <EmptyState
        title={title}
        actions={
          <Button variant="primary" onClick={onRegister}>
            Zum Register
          </Button>
        }
      >
        {text}
      </EmptyState>
    </div>
  );
}
